import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';

interface HiddenFeaturesContextProps {
  children: ReactNode;
}

interface HiddenFeaturesContextValue {
  hiddenFeaturesEnabled: boolean;
  enableHiddenFeatures: () => void;
  disableHiddenFeatures: () => void;
}

const HiddenFeaturesContext = createContext<
  HiddenFeaturesContextValue | undefined
>(undefined);

export const HiddenFeaturesProvider: React.FC<HiddenFeaturesContextProps> = ({
  children,
}) => {
  const [hiddenFeaturesEnabled, setHiddenFeaturesEnabled] =
    useState<boolean>(false);
  const [showMessage, setShowMessage] = useState<boolean>(false);

  const enableHiddenFeatures = useCallback(() => {
    setHiddenFeaturesEnabled(true);
    setShowMessage(true);

    // Hide the message after 3 seconds
    setTimeout(() => {
      setShowMessage(false);
    }, 3000);
  }, []);

  const disableHiddenFeatures = useCallback(() => {
    setHiddenFeaturesEnabled(false);
  }, []);

  // Check if we're on localhost and enable hidden features by default
  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      enableHiddenFeatures();
    }
  }, [enableHiddenFeatures]);

  // Add event listener for Ctrl+H keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+H (or Cmd+H on Mac)
      if ((event.ctrlKey || event.metaKey) && event.key === 'h') {
        event.preventDefault(); // Prevent default browser behavior
        enableHiddenFeatures();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableHiddenFeatures]);

  const value: HiddenFeaturesContextValue = {
    hiddenFeaturesEnabled,
    enableHiddenFeatures,
    disableHiddenFeatures,
  };

  return (
    <HiddenFeaturesContext.Provider value={value}>
      {children}
      {showMessage && (
        <div className="hidden-features-message">hidden features enabled!</div>
      )}
    </HiddenFeaturesContext.Provider>
  );
};

export const useHiddenFeatures = (): HiddenFeaturesContextValue => {
  const context = useContext(HiddenFeaturesContext);

  if (context === undefined) {
    throw new Error(
      'useHiddenFeatures must be used within a HiddenFeaturesProvider',
    );
  }

  return context;
};
